/*
 * Copyright (c) 2022. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import _ from "lodash";
import ApiClient from "./ApiClient";
import { translate } from "./Translations/translatorService";
import { convertToFloat, grossValueToNet } from "./formServiceFunctions";
import { electronicFormatIBAN, isValidIBAN } from "ibantools";
import PasswordValidator from "password-validator";
import { hasCreditPayment } from "./accountingService";
import { isDayjs } from "../types/TypeGuards/typeGuards";
import { getEditorHtml } from "./Editor/editorFunctions";
import { Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import validator from "validator";
import { ProductUnion } from "../types/ProductUnion";
import dayjs, { Dayjs } from "dayjs";

export const isPastDate = (value: string | null) => {
  return (
    _.isEmpty(value) ||
    dayjs(value, ["DD.MM.YYYY", "DD.M.YYYY", "D.MM.YYYY", "D.M.YYYY"], true).isBefore(new Date(), "day")
  );
};

export const isTodayOrFutureDate = (value?: Dayjs | string | null) => {
  if (value === undefined || value === null) {
    return true;
  }
  if (_.isString(value)) {
    return (
      _.isEmpty(value) ||
      dayjs(value, ["YYYY-MM-DD", "DD.MM.YYYY", "DD.M.YYYY", "D.MM.YYYY", "D.M.YYYY"], true).isSameOrAfter(
        new Date(),
        "day"
      )
    );
  }
  return value.isSameOrAfter(new Date(), "day");
};

export const isFutureDate = (value?: Dayjs | string | null) => {
  return (
    _.isEmpty(value) ||
    (isDayjs(value) && value.isAfter(new Date(), "day")) ||
    dayjs(value, ["DD.MM.YYYY", "DD.M.YYYY", "D.MM.YYYY", "D.M.YYYY", "YYYY-MM-DD"], true).isAfter(new Date(), "day")
  );
};

export const isBiggerOrSameDateThan = (value: Dayjs | string | null, referenceDate: string) => {
  // Day.js parses years from 00 to 99 as 1900 to 1999, so restore the explicit year after parsing.
  const parsedReferenceDate = dayjs(referenceDate, "DD.MM.YYYY").year(Number(referenceDate.slice(-4)));

  return (
    _.isEmpty(value) ||
    (isDayjs(value) && value.isSameOrAfter(parsedReferenceDate, "day")) ||
    dayjs(value, ["DD.MM.YYYY", "DD.M.YYYY", "D.MM.YYYY", "D.M.YYYY", "YYYY-MM-DD"], true).isSameOrAfter(
      parsedReferenceDate,
      "day"
    )
  );
};

export const isSmallerOrSameDateThan = (value: Dayjs | string | null, referenceDate: Dayjs) => {
  return (
    _.isEmpty(value) ||
    (isDayjs(value) && value.isSameOrBefore(referenceDate, "day")) ||
    dayjs(value, ["DD.MM.YYYY", "DD.M.YYYY", "D.MM.YYYY", "D.M.YYYY", "YYYY-MM-DD"], true).isSameOrBefore(
      referenceDate,
      "day"
    )
  );
};

export const isValidDate = (value?: Dayjs | string | null) => {
  if (value === undefined || value === null) {
    return true;
  }

  if (_.isString(value)) {
    return (
      _.isEmpty(value) ||
      dayjs(value, ["YYYY-MM-DD", "DD.MM.YYYY", "DD.M.YYYY", "D.MM.YYYY", "D.M.YYYY"], true).isValid()
    );
  }
  return value.isValid();
};

export const matchRegexp = (value: string, regexp: string | RegExp) => {
  const validationRegexp = regexp instanceof RegExp ? regexp : new RegExp(regexp);
  return _.isEmpty(value) || validationRegexp.test(value);
};

export const isMoney = (value: string) => {
  return _.isEmpty(value) || matchRegexp(value, /^-?\d+(\.\d+)*(,\d{1,2})?$/);
};

export const isSafePassword = (value: string) => {
  let schema = new PasswordValidator();
  schema.is().min(8).has().digits().has().letters();
  return schema.validate(value);
};

export const isBeaSafeId = (value: string) => {
  return (_.isEmpty(value) ||
  matchRegexp(value, /^DE\.Justiz\.\w{8}-\w{4}-\w{4}-\w{4}-\w{6}$/) ||
  matchRegexp(value, /^DE\.Justiz\.\w{8}-\w{4}-\w{4}-\w{4}-\w{12}\.\w{4}$/) ||
  matchRegexp(value, /^DE\.Justiztest\.\w{8}-\w{4}-\w{4}-\w{4}-\w{12}\.\w{4}$/) ||
  matchRegexp(value, /^DE\.BRAK(_SPT)?\.\w{8}-\w{4}-\w{4}-\w{4}-\w{12}\.\w{4}$/) ||
  matchRegexp(value, /^govello-\w{13}-\w{9}$/) ||
  matchRegexp(value, /^safe-prod-\w{13}-\w{9}$/) ||
  matchRegexp(value, /^safe-sp1-\w{13}-\w{9}$/) || matchRegexp(value, /^safe-sp1-\w{13}$/));
};

export const isIban = (value: string) => {
  const iban = "DE" + value;
  return _.isEmpty(value) || isValidIBAN(electronicFormatIBAN(iban) as string);
};

export const isValidPostalCode = (value: string) => {
  if (_.isEmpty(value)) {
    return true;
  }
  const v = value.trim();
  return validator.isPostalCode(v, "any");
};

export const isEmail = (value: string) => {
  return matchRegexp(
    value,
    /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)])$/i
  );
};

export const isNotDuplicateMail = async (value: string, originalEmail: string) => {
  //if no change happened do not check for duplicates
  if (value === originalEmail) {
    return true;
  }

  //to prevent backend error when mail has the wrong format
  //if this validator comes before the isEmail validator, this results in the "duplicate email" error message even though it just has the wrong format
  if (!isEmail(value)) {
    return false;
  }

  const emailCheckResponse = await ApiClient.post("check_email", {
    body: JSON.stringify({ email: value.toLowerCase() }),
  });
  return !emailCheckResponse.customerExists;
};

export const isInsurancePickerEditable = async (value: number, product: ProductUnion) => {
  if (value === product.caseInsurance.insurer?.id) {
    return true;
  }

  return !(await hasCreditPayment(product));
};

export const isRequired = (value: any) => {
  return !_.isEmpty(value);
};

export const isNumber = (value: string) => {
  if (!value) {
    return true;
  }
  return matchRegexp(value, /^-?[0-9]\d*(\d+)?$/i);
};

export const isPhoneNumberWithoutSpecialCharacters = (value: string) => {
  return matchRegexp(value, /^0?[1-9]\d+$/);
};

export const isFloat = (value: string) => {
  if (!value) {
    return true;
  }
  return matchRegexp(value, /^(?:-?[1-9]\d*|-?0)?(?:\.\d+)?$/i);
};

export const isNonNegativeFloat = (value: string) => {
  if (!value) {
    return true;
  }
  return matchRegexp(value, /^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/);
};

export const notNone = (value: string | null) => {
  return !(value === "none" || value === "");
};

export const maxNumber = (value: string, max: string) => {
  return _.isEmpty(value) || parseInt(value, 10) <= parseInt(max, 10);
};

export const maxFloat = (value: string, max: string) => {
  return _.isEmpty(value) || parseFloat(value) <= parseFloat(max);
};

export const minFloat = (value: string, min: string) => {
  return _.isEmpty(value) || parseFloat(value) >= parseFloat(min);
};

export const minStringLength = (value: string, length: number) => {
  return _.isEmpty(value) || (_.isString(value) && value.length >= length);
};

export const requiredRelatedField = (value: string, relatedValue: string) => {
  return (_.isEmpty(value) && _.isEmpty(relatedValue)) || (!_.isEmpty(value) && !_.isEmpty(relatedValue));
};

export const atLeastOneFieldRequired = (value: string, relatedValue: string) => {
  return !(_.isEmpty(value) && _.isEmpty(relatedValue));
};

export const numbersDifferent = (value: string, relatedValue: string) => {
  try {
    const valueFloat = parseFloat(value);
    const relatedValueFloat = parseFloat(relatedValue);

    return !isNaN(valueFloat) && !isNaN(relatedValueFloat) && valueFloat !== relatedValueFloat;
  } catch {
    return false;
  }
};

export const convertedGrossValueSmallerThanRelatedNetValue = (value: string, relatedValue: string) => {
  if (_.isNaN(convertToFloat(value)) || _.isNaN(convertToFloat(relatedValue))) {
    return true;
  }
  return grossValueToNet(convertToFloat(value)) <= +convertToFloat(relatedValue).toFixed(2);
};

export const isValidInstituteReference = (value: string) => {
  const instituteReferenceRegEx = /^(?=(?:.*\d){4,})[a-zA-ZäöüÄÖÜß0-9\/&.,\-()+ ]*$/;
  return _.isEmpty(value) || instituteReferenceRegEx.test(value);
};

export const isValidAuthorityReference = (value: string) => {
  const authorityReferenceRegEx = /^[a-zA-ZäöüÄÖÜß0-9\/&.,\-()+ ]*$/;
  return _.isEmpty(value) || authorityReferenceRegEx.test(value);
};

export const postalCodeDefault = {
  validator: isValidPostalCode,
  message: "Die Postleitzahl hat ein ungültiges Format",
};

export const dateFieldDefault = [
  {
    validator: isValidDate,
    message: "Das Datum hat ein falsches Format",
  },
  {
    validator: isBiggerOrSameDateThan,
    params: ["01.01.0001"],
    message: `Wert darf nicht vor dem 01.01.0001 liegen`,
  },
];

export const passwordFieldDefault = [
  {
    validator: isSafePassword,
    message: "Mindestens 8 Zeichen. Muss Zahlen und Buchstaben enthalten.",
  },
];

export const futureDateDefault = [
  {
    validator: isFutureDate,
    message: "Das Datum muss in der Zukunft liegen",
  },
];

export const intFieldDefault = [
  {
    validator: isNumber,
    message: "Die Eingabe muss eine Ganzzahl sein",
  },
];

export const floatFieldNonNegativeDefault = [
  {
    validator: isNonNegativeFloat,
    message: "Die Eingabe muss eine positive Zahl mit maximal zwei Nachkommastellen sein.",
  },
];

export const floatFieldDefault = [
  {
    validator: isFloat,
    message: "Die Eingabe muss eine Zahl sein und darf maximal zwei Nachkommastellen haben.",
  },
];

export const instituteReferenceDefault = [
  {
    validator: isValidInstituteReference,
    message: "Das Feld darf keine Sonderzeichen enthalten und muss mindestens 4 Ziffern enthalten.",
  },
];

export const authorityReferenceDefault = [
  {
    validator: isValidAuthorityReference,
    message: "Das Feld enthält nicht erlaubte Zeichen.",
  },
];

export const beaSafeIdFieldDefault = [
  {
    validator: isBeaSafeId,
    message: "Dies ist keine gültige beA Safe-ID.",
  },
];

export const notNoneSelectValidator = [
  {
    validator: notNone,
    message: "Bitte treffen Sie eine Auswahl",
  },
];

export const percentageFieldDefault = [
  {
    validator: maxFloat,
    params: [100],
    message: "Die Eingabe muss eine Zahl zwischen 0 und 100 sein",
  },
  {
    validator: minFloat,
    params: [0],
    message: "Die Eingabe muss eine Zahl zwischen 0 und 100 sein",
  },
];

export const moneyFieldDefault = [
  {
    validator: isMoney,
    message: "Die Eingabe ist kein Währungswert",
  },
];

export const ibanFieldDefault = [
  {
    validator: isIban,
    message: "Die eingegebene IBAN ist nicht korrekt",
  },
];

export const requiredFieldDefault = [
  {
    validator: isRequired,
    message: "Dies ist ein Pflichtfeld",
  },
];

export const emailDefault = [
  {
    validator: isEmail,
    message: "Dies ist keine gültige E-Mail Adresse",
  },
];

export const duplicateMailDefault = (originalEmail: string) => [
  {
    validator: isNotDuplicateMail,
    params: [originalEmail],
    message: "Diese E-Mail Adresse existiert bereits",
  },
];

export const insurancePickerValidator = (product: ProductUnion) => [
  {
    validator: isInsurancePickerEditable,
    params: [product],
    message: "Die Versicherung kann nicht mehr geändert werden.",
  },
];

export const biggerOrSameDateThanDefault = (referenceDate: string, relatedFieldLabel: string) => [
  {
    validator: isBiggerOrSameDateThan,
    params: [referenceDate],
    message: `Das Datum muss nach dem Datum von ${relatedFieldLabel} liegen`,
  },
];

export const smallerOrSameDateThanDefault = (referenceDate: Dayjs, relatedFieldLabel: string) => [
  {
    validator: isSmallerOrSameDateThan,
    params: [referenceDate],
    message: `Das Datum muss vor dem Datum von ${relatedFieldLabel} liegen`,
  },
];

export const textFieldDefault = [
  {
    validator: minStringLength,
    params: [2],
    message: "Der Text ist zu kurz",
  },
];

export const isPhoneNumberWithoutSpecialCharactersDefault = [
  {
    validator: isPhoneNumberWithoutSpecialCharacters,
    message: "Die Telefonnummer darf keine Sonderzeichen enthalten oder mehrere führende Nullen haben.",
  },
];

export const faxNumberDefault = [
  {
    validator: matchRegexp,
    params: [new RegExp(/^[1-9]\d+$/)],
    message: "Bitte das Format beachten (Nur Ziffern ohne führende Null)",
  },
];

export const relatedFieldDefault = (relatedFieldName: string, translationPath = "") => {
  translationPath = translationPath || relatedFieldName;
  return [
    {
      validator: requiredRelatedField,
      relatedField: relatedFieldName,
      message: `${translate(translationPath + ".label")} muss auch gefüllt/leer sein`,
    },
  ];
};

export const atLeastOneFieldRequiredDefault = (relatedFieldName: string) => {
  return [
    {
      validator: atLeastOneFieldRequired,
      relatedField: relatedFieldName,
      message: `Dieses Feld oder ${translate(relatedFieldName + ".label")} muss ausgefüllt werden`,
    },
  ];
};

export const invoiceNetValueNotOverpaid = (relatedFieldName: string) => {
  return [
    {
      validator: convertedGrossValueSmallerThanRelatedNetValue,
      relatedField: relatedFieldName,
      message: `Dieser Bruttozahlbetrag ist höher als die Bruttorechnungssumme, die sich aus dem Nettowert aus dem Feld
      "${translate(relatedFieldName + ".label")}" ergibt.`,
    },
  ];
};

export const requiredValidator = (message: string) => {
  return {
    validator: isRequired,
    message,
  };
};

export const caseReferenceNumberIsUnique = async (value: string, originalValue: string, productId?: number) => {
  if (value === originalValue) {
    return true;
  }

  let payload: { value: string; productId?: number } = {
    value: value,
  };
  if (productId) {
    payload.productId = productId;
  }

  const validateResponse = await ApiClient.post("/lb/validator/case_insurance/case_reference_number", {
    body: JSON.stringify(payload),
  });

  return validateResponse?.isValid;
};

export const tipTapEditorHasContent = (value: string) => {
  return !!getEditorHtml(new Editor({ content: value, extensions: [StarterKit] }), true);
};

export const tipTapEditorDefault = [
  {
    validator: tipTapEditorHasContent,
    message: "Bitte geben Sie mindestens 1 Zeichen ein",
  },
];

export const emailArray = () => {
  return [
    {
      validator: (arr: Record<string, string>[]) => {
        let hasInvalidEmail = arr.map((obj) => !isEmail(obj.email)).includes(true);

        return !hasInvalidEmail;
      },
      message: "Die E-Mail Adresse ist nicht valide.",
    },
  ];
};
